import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import PasswordInput from '../components/PasswordInput';
import { API_ORIGIN } from '../lib/platform';

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const token = params.get('token');

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('Missing invite token');
      setLoading(false);
      return;
    }
    fetch(`${API_ORIGIN}/api/invites/${token}`)
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Invite not found');
        setInvite(body);
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_ORIGIN}/api/invites/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to accept invite');

      await signIn(body.email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="flex-1 flex items-center justify-center bg-[#F6F5FA] px-6 py-12 lg:py-0 lg:w-1/2">
        <div className="w-full max-w-[400px] lg:bg-white lg:rounded-[28px] lg:p-10 lg:shadow-[0_1px_2px_rgba(17,12,34,0.04),0_12px_32px_rgba(17,12,34,0.08)]">
          <div className="text-center mb-6">
            <img
              src="/cscore-logo.png"
              alt="CScore"
              className="h-10 mx-auto"
              style={{ filter: 'brightness(0) saturate(100%) invert(13%) sepia(90%) saturate(5500%) hue-rotate(270deg) brightness(80%) contrast(110%)' }}
            />
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 text-center">Loading invite...</p>
          ) : loadError ? (
            <div className="text-center">
              <h2 className="text-[30px] font-bold tracking-[-0.02em] text-[#111] mb-3">Invite Unavailable</h2>
              <p className="text-sm text-gray-600 mb-6">{loadError}</p>
              <Link to="/login" className="text-primary font-medium text-sm hover:underline">
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-[30px] font-bold tracking-[-0.02em] text-[#111] text-center mb-2">Accept Invite</h2>
              <p className="text-sm text-gray-600 text-center mb-6">
                Hi {invite.first_name || 'there'}, set a password to join{' '}
                <strong>{invite.clinic_name || 'your clinic'}</strong>.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-[14px] font-semibold text-[#111] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={invite.email}
                    disabled
                    className="w-full h-12 border border-[#E5E2EE] rounded-[14px] px-4 text-[16px] md:text-[15px] bg-[#F3F1F8] text-gray-600"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-[14px] font-semibold text-[#111] mb-1.5">Password</label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    className="w-full h-12 bg-white border border-[#E5E2EE] rounded-[14px] px-4 text-[16px] md:text-[15px] text-[#111] focus:outline-none focus:ring-4 focus:ring-primary-10 focus:border-primary-40 transition"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-[14px] font-semibold text-[#111] mb-1.5">Confirm Password</label>
                  <PasswordInput
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full h-12 bg-white border border-[#E5E2EE] rounded-[14px] px-4 text-[16px] md:text-[15px] text-[#111] focus:outline-none focus:ring-4 focus:ring-primary-10 focus:border-primary-40 transition"
                  />
                </div>

                {error && (
                  <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 rounded-[14px] bg-primary text-white font-semibold text-[15px] shadow-[0_8px_20px_rgba(108,24,201,0.28)] hover:bg-primary-80 transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Setting up your account...' : 'Set Password & Sign In'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <div
        className="hidden lg:flex lg:w-1/2 lg:m-4 lg:rounded-[32px] flex-col items-center justify-center px-12"
        style={{ background: 'radial-gradient(120% 90% at 0% 0%, #8B3FE6 0%, #6C18C9 45%, #3E0A78 100%)' }}
      >
        <h2 className="text-white text-4xl font-bold mb-4 text-center">Measure What Matters</h2>
        <p className="text-white/80 text-lg text-center max-w-md leading-relaxed">
          Track clinician performance, improve patient outcomes, and make data-driven decisions for your treatment center.
        </p>
      </div>
    </div>
  );
}
